// ============================================================================
// CompensationModal — writes a NEW compensation period, it does not overwrite.
//
// The three quick actions the mockup listed separately (Change hours, Change
// wage rate, Change pay frequency) are one form here on purpose: they share an
// effective date, and saving them one at a time would mint three periods for
// what HR thinks of as one change. The mockup's three buttons were three
// toasts; three real writes would be a worse answer than one.
//
// hr_set_employee_compensation decides what happens from the date:
//   later than the current period  → supersedes it (history preserved)
//   same day as the current period → corrects it in place
//   earlier                        → refused, PT422, and says why
// ============================================================================
import { useState, type FormEvent } from 'react';
import { X as XIcon } from 'lucide-react';
import { hrSetCompensation, type CompHistoryRow } from './hooks/use-hr-data';
import { overlayClickClose } from '../../shared/overlay-close';
import { errorMessage } from '../../shared/errors';

const PAY_FREQUENCIES = ['Weekly', 'Biweekly', 'Semimonthly', 'Monthly'];

interface Props {
  employeeId: string;
  current: CompHistoryRow | null;
  payType: string | null;
  onClose: () => void;
  onSaved: () => void;
}

function today(): string {
  return new Date().toISOString().slice(0, 10);
}

export default function CompensationModal({
  employeeId,
  current,
  payType,
  onClose,
  onSaved,
}: Props) {
  const [effective, setEffective] = useState(today());
  const [rate, setRate] = useState(current?.wage_rate != null ? String(current.wage_rate) : '');
  const [freq, setFreq] = useState(current?.pay_frequency ?? 'Biweekly');
  const [hours, setHours] = useState(
    current?.standard_hours != null ? String(current.standard_hours) : '',
  );
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const hourly = payType === 'Hourly';

  async function handleSubmit(ev: FormEvent) {
    ev.preventDefault();
    const rateNum = rate.trim() === '' ? null : Number(rate);
    const hoursNum = hours.trim() === '' ? null : Number(hours);

    if (rateNum !== null && !Number.isFinite(rateNum)) {
      setError('Wage rate must be a number.');
      return;
    }
    if (hoursNum !== null && !Number.isFinite(hoursNum)) {
      setError('Standard hours must be a number.');
      return;
    }

    setSubmitting(true);
    setError(null);
    try {
      await hrSetCompensation({
        employee_id: employeeId,
        effective_date: effective,
        pay_type: payType,
        wage_rate: rateNum,
        pay_frequency: freq || null,
        standard_hours: hoursNum,
        notes: notes.trim() || null,
      });
      onSaved();
      onClose();
    } catch (err) {
      console.error('[hr] set compensation failed', err);
      setError(errorMessage(err, 'Could not save the change.'));
      setSubmitting(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-[70] flex items-end justify-center bg-tcs-scrim/60 sm:items-center"
      role="dialog"
      aria-modal="true"
      aria-label="Change compensation"
      {...overlayClickClose(onClose)}
    >
      <form
        onClick={(ev) => ev.stopPropagation()}
        onSubmit={handleSubmit}
        className="max-h-[92vh] w-full max-w-md overflow-y-auto rounded-t-xl bg-tcs-overlay p-5 shadow-xl sm:rounded-xl"
      >
        <div className="flex items-start justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold text-tcs-text">Change compensation</h2>
            <p className="text-xs text-tcs-text-muted">
              {employeeId} · {payType ?? 'pay type not set'}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="rounded-md p-1 text-tcs-text-muted hover:bg-tcs-surface"
          >
            <XIcon className="h-4 w-4" />
          </button>
        </div>

        {current && (
          <p className="mt-3 rounded-md border border-tcs-border bg-tcs-surface p-2 text-xs text-tcs-text-secondary">
            Current period started {current.effective_date}. A later date starts a new period and
            keeps this one in history; the same date corrects it.
          </p>
        )}

        <div className="mt-4 space-y-4">
          <Field label="Effective date">
            <input
              type="date"
              required
              value={effective}
              onChange={(ev) => setEffective(ev.target.value)}
              className={inputCls}
            />
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label={hourly ? 'Hourly rate' : 'Annual salary'}>
              <input
                inputMode="decimal"
                value={rate}
                onChange={(ev) => setRate(ev.target.value)}
                placeholder={hourly ? '18.50' : '82000'}
                className={inputCls}
              />
            </Field>
            <Field label="Standard hours / week">
              <input
                inputMode="decimal"
                value={hours}
                onChange={(ev) => setHours(ev.target.value)}
                placeholder="40"
                className={inputCls}
              />
            </Field>
          </div>

          <Field label="Pay frequency">
            <select value={freq} onChange={(ev) => setFreq(ev.target.value)} className={inputCls}>
              {(PAY_FREQUENCIES.includes(freq) ? PAY_FREQUENCIES : [freq, ...PAY_FREQUENCIES]).map(
                (f) => (
                  <option key={f} value={f}>
                    {f}
                  </option>
                ),
              )}
            </select>
          </Field>

          <Field label="Notes">
            <textarea
              rows={2}
              value={notes}
              onChange={(ev) => setNotes(ev.target.value)}
              placeholder="Annual review, promotion, correction…"
              className={inputCls}
            />
          </Field>
        </div>

        {error && (
          <p className="mt-3 rounded-md border border-st-danger-border bg-st-danger-bg p-2 text-xs text-st-danger-fg-strong">
            {error}
          </p>
        )}

        <div className="mt-5 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-tcs-border px-3 py-2 text-sm text-tcs-text-secondary hover:bg-tcs-bg-muted"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={submitting}
            className="rounded-md bg-tcs-inverse px-4 py-2 text-sm font-medium text-tcs-inverse-ink hover:bg-tcs-inverse-hover disabled:opacity-50"
          >
            {submitting ? 'Saving…' : 'Save change'}
          </button>
        </div>
      </form>
    </div>
  );
}

const inputCls =
  'w-full rounded-md border border-tcs-border bg-tcs-field px-3 py-2 text-base focus:border-tcs-border-input focus:outline-none';

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block text-xs">
      <span className="mb-1 block font-medium uppercase tracking-wide text-tcs-text-muted">
        {label}
      </span>
      {children}
    </label>
  );
}
